import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { userActions } from '../redux/slices/userSlice';
import userService from '../services/UserServices';
import { UserPostFields, UserMode } from '../redux/types/userTypes';
import { FORM_CONSTANTS, USER_CONSTANTS } from '../config/constants';
import { useApiErrorHandler } from './useApiErrorHandler';

// ✅ INTERFACE MEJORADA CON VALIDACIONES
export interface RegistrationFormData {
  RUT: string;
  RUTError: string;
  name: string;
  lastName: string;
  password: string;
  confirmation: string;
  passwordError: string;
  weight: string;
  height: string;
  birthday: string | null;
  sex: string;
  diabetes: boolean;
  thrombosis: boolean;
  heartDisease: boolean;
  otherDiseases: string;
  phone: string;
  phoneError: string;
  activationCode: string;
  businessId?: number; // ✅ Campo para el ID del negocio seleccionado
}

const initialFormData: RegistrationFormData = {
  RUT: '',
  RUTError: '',
  name: '',
  lastName: '',
  password: '',
  confirmation: '',
  passwordError: '',
  weight: '',
  height: '',
  birthday: null,
  sex: '',
  diabetes: false,
  thrombosis: false,
  heartDisease: false,
  otherDiseases: '',
  phone: FORM_CONSTANTS.PHONE_PREFIX,
  phoneError: '',
  activationCode: '',
  businessId: undefined, // ✅ Inicializar businessId
};

// ✅ TIPO DE RETORNO EXPLÍCITO DEL HOOK
export interface UseRegistrationFormReturn {
  formData: RegistrationFormData;
  updateField: (field: keyof RegistrationFormData, value: any) => void;
  updateMultipleFields: (updates: Partial<RegistrationFormData>) => void;
  resetForm: () => void;
  submitRegistration: (
    businessId: number,
    activationCode: string,
    termId: number
  ) => Promise<any>;
  isFormValid: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  passwordConfirmationError: string;
}

export const useRegistrationForm = (): UseRegistrationFormReturn => {
  const dispatch = useDispatch();
  const [formData, setFormData] =
    useState<RegistrationFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ OBTENER BUSINESS DESDE REDUX
  const businessIdFromRedux = useSelector(
    (state: RootState) => state.business.id
  );

  // ✅ USAR EL NUEVO SISTEMA DE MANEJO DE ERRORES
  const {
    error: submitError,
    handleApiError,
    clearError,
  } = useApiErrorHandler();

  // ✅ INICIALIZAR businessId DESDE REDUX SI ESTÁ DISPONIBLE Y NO SE HA ESTABLECIDO
  useEffect(() => {
    if (businessIdFromRedux && !formData.businessId) {
      console.log(
        '🔍 Initializing businessId from Redux:',
        businessIdFromRedux,
        'formData.businessId',
        formData.businessId
      );
      setFormData(prev => ({ ...prev, businessId: businessIdFromRedux }));
    }
  }, [businessIdFromRedux, formData.businessId]);

  // ✅ VALIDACIÓN DE CONFIRMACIÓN DE CONTRASEÑA
  const passwordConfirmationError = useMemo(() => {
    if (formData.confirmation && formData.password !== formData.confirmation) {
      return 'Las contraseñas no coinciden';
    }
    return '';
  }, [formData.password, formData.confirmation]);

  // ✅ VALIDACIÓN COMPLETA DEL FORMULARIO
  const isFormValid = useMemo(() => {
    return Boolean(
      formData.RUT &&
        formData.name &&
        formData.lastName &&
        formData.password &&
        formData.weight &&
        formData.height &&
        formData.birthday &&
        formData.sex &&
        formData.phone &&
        !formData.RUTError &&
        !formData.passwordError &&
        !formData.phoneError &&
        !passwordConfirmationError
    );
  }, [formData, passwordConfirmationError]);

  const updateField = useCallback(
    (field: keyof RegistrationFormData, value: any) => {
      console.log(`🔄 Updating field ${field} with value:`, value);
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        console.log(`✅ New formData:`, newData);
        // ✅ VALIDACIÓN SIMPLIFICADA - SOLO LIMPIAR ERRORES EXISTENTES
        if (field === 'RUT' && formData.RUTError) {
          newData.RUTError = '';
        }
        if (field === 'password' && formData.passwordError) {
          newData.passwordError = '';
        }
        if (field === 'phone' && formData.phoneError) {
          newData.phoneError = '';
        }
        return newData;
      });
      // ✅ LIMPIAR ERRORES DE SUBMIT AL MODIFICAR
      if (submitError) {
        clearError();
      }
    },
    [
      submitError,
      formData.RUTError,
      formData.passwordError,
      formData.phoneError,
      clearError,
    ]
  );

  const updateMultipleFields = useCallback(
    (updates: Partial<RegistrationFormData>) => {
      setFormData(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    clearError();
    setIsSubmitting(false);
  }, [clearError]);

  // ✅ SUBMIT MEJORADO CON MANEJO DE ERRORES UNIFICADO
  const submitRegistration = useCallback(
    async (businessId: number, activationCode: string, termId: number) => {
      if (!isFormValid) {
        const errorMessage =
          'Por favor, complete todos los campos requeridos correctamente';
        handleApiError(new Error(errorMessage));
        return { success: false, error: 'Formulario inválido' };
      }

      setIsSubmitting(true);
      clearError();

      try {
        const userData: UserPostFields = {
          email: '', // TODO: Agregar campo email al formulario
          rut: formData.RUT,
          password: formData.password,
          first_name: formData.name,
          last_name: formData.lastName,
          height: +formData.height,
          weight: +formData.weight,
          birthday: formData.birthday || '',
          sex: formData.sex,
          diabetes: formData.diabetes,
          heart_diseases: formData.heartDisease,
          other_diseases: formData.otherDiseases,
          thrombosis: formData.thrombosis,
          business_id: businessId,
          phone: formData.phone,
        };

        const response = await userService.createUser(
          userData,
          activationCode,
          termId
        );

        if (response.user) {
          const { user } = response;
          user.token = response.token;
          user.mode = USER_CONSTANTS.DEFAULT_MODE as UserMode;
          dispatch(userActions.login(user));
          return { success: true, user };
        }

        const errorMessage =
          'Error al crear usuario. Por favor, intente nuevamente.';
        handleApiError(new Error(errorMessage));
        return { success: false, error: response };
      } catch (error) {
        handleApiError(error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
        };
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, isFormValid, dispatch, handleApiError, clearError]
  );

  return {
    formData,
    updateField,
    updateMultipleFields,
    resetForm,
    submitRegistration,
    isFormValid,
    isSubmitting,
    submitError: submitError?.message || null,
    passwordConfirmationError,
  };
};
